import * as React from "react";
import { PlayerCardWidgetOwner } from "../../../../../services/bedrock";
import { Dialog } from "@/components/ui";
import { getWidgetIcon, getWidgetTitle } from "../../../utils";
import { cn } from "@/lib/utils";
import { CoachSettings, WebSettings } from "@/interfaces";
import { useAccountSettings, usePlayerSettings, useSocialCoachSession } from "@/utils";

interface NewWidgetModalProps {
  open: boolean;
  onClose: () => void;
  onNewWidgetOwnerSelected: (widget: PlayerCardWidgetOwner) => void;
}

const WIDGET_DESCRIPTIONS: Record<PlayerCardWidgetOwner["type"], string> = {
  Link: "Redirect visitors to any external URL with a custom title and thumbnail.",
  SocialMedia: "Display icons for connected social profiles to grow your following.",
  ContentPage: "Feature your video pages directly on your card.",
  Post: "Showcase a specific post or prompt to drive targeted engagement.",
  SmartLink: "Use trackable links with automatic UTM parameters for deep analytics.",
  Phone: "Let visitors call you directly with a one-tap phone link.",
  Email: "Make it easy for prospects to reach out via their favorite email client.",
  Address: "Share your physical location or office address with map integration.",
  Contact: "A streamlined button for visitors to save your contact info or reach out.",
  Header: "The top section of your card with your profile and banner.",
  Footer: "The bottom section for disclaimers and brand logos.",
  Blog: "Feature a published blog so visitors can read your latest article.",
  Review: "Show featured reviews, a specific review, or your star, NPS or satisfaction rating.",
};

const NewWidgetModal: React.FC<NewWidgetModalProps> = ({ open, onClose, onNewWidgetOwnerSelected }) => {
  const { coachId, playerId, isPlayer } = useSocialCoachSession();
  const { hasAccountSetting } = useAccountSettings(coachId);
  const { hasSetting: hasPlayerSetting } = usePlayerSettings(isPlayer ? playerId : null);
  const hasReputationManagement =
    hasAccountSetting(WebSettings.REPUTATION_MANAGEMENT) &&
    (!isPlayer || hasPlayerSetting(CoachSettings.PLAYER_REPUTATION_MANAGEMENT));

  const availableWidgetOwners: PlayerCardWidgetOwner[] = [
    {
      id: -Date.now(),
      type: "Link",
      thumbnailMedia: [],
      title: "",
      url: "",
    },
    {
      id: -Date.now(),
      type: "SmartLink",
      selectionType: "RANDOM",
      styleType: "BUTTON",
      smartLinkId: null,
      smartLink: null,
      title: null,
    },
    {
      id: -Date.now(),
      type: "SocialMedia",
      displayFacebook: true,
      displayInstagram: true,
      displayLinkedIn: true,
      displayX: true,
      displayTikTok: true,
      displayGoogle: true,
      displayZillow: true,
    },
    {
      id: -Date.now(),
      type: "ContentPage",
      selectionType: "RANDOM",
      contentPage: undefined,
      contentPageId: null,
      title: null,
    },
    {
      id: -Date.now(),
      type: "Blog",
      selectionType: "MOST_RECENT",
      blog: undefined,
      blogId: null,
      title: null,
      subtitle: null,
    },
    {
      id: -Date.now(),
      type: "Post",
      selectionType: "RANDOM",
      post: undefined,
      postId: null,
      title: null,
    },
    {
      id: -Date.now(),
      type: "Phone",
      phoneType: null,
      displayPhoneType: true,
      phoneNumber: "",
      title: null,
    },
    {
      id: -Date.now(),
      type: "Email",
      emailType: null,
      displayEmailType: true,
      email: "",
      title: null,
    },
    {
      id: -Date.now(),
      type: "Address",
      addressType: null,
      displayAddressType: true,
      streetLine1: "",
      streetLine2: null,
      city: "",
      state: "",
      zip: "",
      country: "United States",
      title: null,
    },
    {
      id: -Date.now(),
      type: "Contact",
      title: null,
    },
    ...(hasReputationManagement
      ? [
          {
            id: -Date.now(),
            type: "Review" as const,
            displayType: "FEATURED_REVIEWS" as const,
            reviewId: null,
            sourceSystems: [],
            autoScrollEnabled: true,
            useCondensedQuotes: true,
            minScore: null,
            linkToReviewPage: true,
          },
        ]
      : []),
  ];

  return (
    <Dialog open={open} onOpenChange={onClose} size="xl">
      <Dialog.Header>
        <Dialog.Title>Insert New Widget</Dialog.Title>
        <Dialog.Description>
          Choose a widget type to add to your Player Card. You can customize the details in the next step.
        </Dialog.Description>
      </Dialog.Header>
      <Dialog.Content className="max-h-[70vh] overflow-y-auto pr-2 no-scrollbar">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {availableWidgetOwners.map((widgetOwner, index) => {
            const IconComponent = getWidgetIcon(widgetOwner);
            const title = getWidgetTitle(widgetOwner.type);
            const description = WIDGET_DESCRIPTIONS[widgetOwner.type];

            return (
              <button
                key={`widget-type-${index}`}
                className={cn(
                  "flex flex-col items-start text-left p-4 rounded-xl border border-slate-200 cursor-pointer",
                  "bg-white hover:bg-slate-50 hover:border-primary transition-all group outline-none focus-visible:ring-2 focus-visible:ring-primary shadow-sm"
                )}
                onClick={() => onNewWidgetOwnerSelected(widgetOwner)}
              >
                <div className="flex items-center gap-3 mb-3">
                  <div className="bg-slate-100 p-2 rounded-lg group-hover:bg-primary/10 group-hover:text-primary transition-colors shrink-0">
                    <IconComponent className="size-5" />
                  </div>
                  <span className="text-lg font-bold text-slate-900 group-hover:text-primary transition-colors">
                    {title}
                  </span>
                </div>
                <div className="text-sm text-muted-foreground leading-relaxed font-sans">{description}</div>
              </button>
            );
          })}
        </div>
      </Dialog.Content>
    </Dialog>
  );
};
export default NewWidgetModal;
